import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import { DocumentDuplicateIcon } from "@heroicons/react/20/solid";
import { ArrowUpOnSquareStackIcon } from "@heroicons/react/24/outline";
import { UseFormReturn } from "react-hook-form";
import { IClinicOnboardProps } from "./interface";

const RemoveIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
    <path
      d="M14.25 5.3075L13.1925 4.25L9 8.4425L4.8075 4.25L3.75 5.3075L7.9425 9.5L3.75 13.6925L4.8075 14.75L9 10.5575L13.1925 14.75L14.25 13.6925L10.0575 9.5L14.25 5.3075Z"
      fill="black"
    />
  </svg>
);

interface IClinicOnboardImagesStepProps {
  formHook: UseFormReturn<IClinicOnboardProps>;
  listcategories: Record<string, string[]>;
  detailcategories: Record<string, string[]>;
  isSubmit: boolean;
  defaultCategories: string[];
  clinicsOrgItem: ISelectMenuItemData[];
  handleImageUpload: (event: React.ChangeEvent<HTMLInputElement>, key: string, category: string) => void;
  handleRemoveImage: (key: string, category: string, i: number) => void;
  handleRemoveAll: (key: string, category: string) => void;
  handleDeleteCategory: (key: string, category: string) => void;
  copyImageModal: () => void;
  imageCategoryModal: () => void;
}

const ClinicOnboardImagesStep = ({
  formHook,
  listcategories,
  detailcategories,
  isSubmit,
  defaultCategories,
  clinicsOrgItem,
  handleImageUpload,
  handleRemoveImage,
  handleRemoveAll,
  handleDeleteCategory,
  copyImageModal,
  imageCategoryModal,
}: IClinicOnboardImagesStepProps) => {
  const renderDefaultCategoryImages = (pageKey: any, gapClass: string) =>
    defaultCategories.map((category, i) => (
      <div key={i}>
        <div className="justify-start items-center gap-8 inline-flex">
          <div className="w-[100px] justify-start items-start gap-1 flex">
            <div>
              <span className="text-black text-xs font-medium">{category}</span>
              <span className="text-rose-600 text-xs font-medium">*</span>
            </div>
          </div>
          <div className="flex-col justify-start items-start gap-10 inline-flex">
            <label
              htmlFor={`${pageKey}-${category}`}
              className="pr-[26px] cursor-pointer items-center gap-[3.73px] flex"
            >
              <div className="text-blue-600">
                <ArrowUpOnSquareStackIcon className="w-4 h-4" />
              </div>
              <input
                type="file"
                id={`${pageKey}-${category}`}
                name={`${pageKey}-${category}`}
                accept=".png"
                onChange={(e) => {
                  handleImageUpload(e, pageKey, category);
                }}
                className="hidden"
                multiple
              />
              <div className="text-center text-blue-600 text-xs font-medium">Upload</div>
            </label>
          </div>
          {formHook.watch(pageKey)?.[category]?.length > 1 && (
            <div className="flex-col justify-start items-start gap-10 inline-flex">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleRemoveAll(pageKey, category);
                }}
              >
                <div className="text-center text-blue-600 text-xs font-medium">Remove All</div>
              </button>
            </div>
          )}
          {isSubmit &&
            (formHook.watch(pageKey)?.[category] == undefined || formHook.watch(pageKey)?.[category]?.length == 0) && (
              <div className="text-[9px]  mt-1 text-red-500">{category} Image is required</div>
            )}
        </div>
        {(formHook.watch(pageKey)?.[category] || false) && (
          <div className={`flex ${gapClass} overflow-scroll`}>
            {formHook.watch(pageKey)?.[category].map((img: string, j: number) => (
              <div className="mt-3 relative" key={j}>
                <img src={img} alt="Selected Image" className="w-32 h-32" />
                <button
                  className="absolute top-0 bg-transparent border-none text-black text-para-fontsize cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    handleRemoveImage(pageKey, category, j);
                  }}
                >
                  <RemoveIcon />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    ));

  const renderCustomCategoryImages = (categoriesObj: Record<string, string[]>, categoryKey: any, gapClass: string) =>
    Object.entries(categoriesObj).map(([key, imgs], i) => {
      const images = imgs as string[];
      return (
        <div key={i}>
          <div className="justify-start items-center gap-8 inline-flex">
            <div className="w-[100px] justify-start items-start gap-1 flex">
              <div>
                <span className="text-black text-xs font-medium">{key}</span>
                <span className="text-rose-600 text-xs font-medium">*</span>
              </div>
            </div>
            <div className="flex-col justify-start items-start gap-10 inline-flex">
              <label
                htmlFor={`${categoryKey}-${key}`}
                className="pr-[26px] cursor-pointer items-center gap-[3.73px] flex"
              >
                <div className="text-blue-600">
                  <ArrowUpOnSquareStackIcon className="w-4 h-4" />
                </div>
                <input
                  type="file"
                  id={`${categoryKey}-${key}`}
                  name={`${categoryKey}-${key}`}
                  accept=".png"
                  onChange={(e) => {
                    handleImageUpload(e, categoryKey, key);
                  }}
                  className="hidden"
                  multiple
                />
                <div className="text-center text-blue-600 text-xs font-medium">Upload</div>
              </label>
            </div>
            {images?.length > 1 && (
              <div className="flex-col justify-start items-start gap-10 inline-flex">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handleRemoveAll(categoryKey, key);
                  }}
                >
                  <div className="text-center text-blue-600 text-xs font-medium">Remove All</div>
                </button>
              </div>
            )}
            <div className="flex-col justify-start items-start gap-10 inline-flex">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleDeleteCategory(categoryKey, key);
                }}
              >
                <div className="text-center text-blue-600 text-xs font-medium">Delete Category</div>
              </button>
            </div>
            {isSubmit && (categoriesObj?.[key] == undefined || categoriesObj?.[key]?.length == 0) && (
              <div className="text-[9px]  mt-1 text-red-500">{key} Image is required</div>
            )}
          </div>
          <div className={`flex ${gapClass} overflow-scroll`}>
            {images?.length > 0 &&
              images?.map((img, j) => (
                <div className="mt-3 relative" key={j}>
                  <img src={img} alt="Selected Image" className="w-32 h-32" />
                  <button
                    className="absolute top-0 bg-transparent border-none text-black text-para-fontsize cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      handleRemoveImage(categoryKey, key, j);
                    }}
                  >
                    <RemoveIcon />
                  </button>
                </div>
              ))}
          </div>
        </div>
      );
    });

  return (
    <>
      <div className="px-3 py-[9px] w-full bg-white rounded-xl shadow">
        <div className="px-3 justify-between items-center flex">
          <div className="text-black text-base font-semibold">Clinic Images Setup</div>
          <button
            onClick={(e) => {
              e.preventDefault();
              copyImageModal();
            }}
            className="h-9 px-4 bg-sky-900 rounded-md border justify-start items-center gap-2 flex"
          >
            <div className="text-white">
              <DocumentDuplicateIcon className="w-4 h-4" />
            </div>
            <div className="text-white text-xs font-semibold">Copy from other clinic</div>
          </button>
        </div>
      </div>
      <div className="mt-5 py-2 bg-white flex-col gap-1.5 flex">
        <div className="pl-3 gap-16  justify-start items-center inline-flex">
          <div className="justify-start items-center gap-9 flex">
            <div className="text-black text-sm font-medium">
              Which Clinic to clone from? <span className="text-red-500">*</span>
            </div>
          </div>
          <div className="h-9 w-[300px] mt-2">
            <NonSearchableSelectMenu
              errorMessages={[]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("clinic_cloning_id", item.id);
                }
              }}
              fieldError={formHook.formState.errors.clinic_cloning_id}
              register={formHook.register("clinic_cloning_id", { required: true })}
              selectItems={clinicsOrgItem}
              placeholder="Select Clinic"
              showTooltips={false}
              showTypedErrors={false}
              showDropdownIcon={true}
            />
          </div>
        </div>
      </div>
      <div className="mt-5 py-2 bg-white flex-col gap-1.5 flex">
        <div className="pl-3 py-[7px] border-b border-slate-400 border-opacity-20 justify-start items-center inline-flex">
          <div className="justify-start items-center gap-9 flex">
            <div className="text-black text-sm font-medium">Enter Non-Category Images</div>
          </div>
        </div>
      </div>
      <div className="justify-start items-start gap-10 inline-flex">
        <div className="p-2.5 justify-start items-start gap-2.5 flex">
          <div>
            <span className="text-black text-xs font-normal">
              Image version 1<br />
            </span>
            <span className="text-slate-400 text-[10px] font-medium">(.png format, 328 Px X 160 Px and 50 KB)</span>
          </div>
        </div>
        {!formHook.watch("image") && (
          <div className="py-5 flex-col justify-start items-start gap-10 inline-flex">
            <label htmlFor="imageUpload" className="pr-[26px] cursor-pointer items-center gap-[3.73px] flex">
              <div className="text-blue-600">
                <ArrowUpOnSquareStackIcon className="w-4 h-4" />
              </div>
              <input
                type="file"
                id="imageUpload"
                name="image"
                accept=".png"
                onChange={(e) => handleImageUpload(e, "image", "")}
                className="hidden"
              />
              <div className="text-center text-blue-600 text-xs font-medium">Upload</div>
            </label>
          </div>
        )}
        {(formHook.watch("image") || false) && (
          <div className="mt-3 relative">
            <img src={`${formHook.watch("image")}`} className="w-32 h-32" />
            <button
              className="absolute top-0 bg-transparent border-none text-black text-para-fontsize cursor-pointer"
              onClick={(event) => {
                event.preventDefault();
                handleRemoveImage("image", "", 0);
              }}
            >
              <RemoveIcon />
            </button>
          </div>
        )}
      </div>
      <div className="pl-3 mt-10 py-[7px] justify-between border-b border-slate-400 border-opacity-20 flex">
        <div className="items-center flex">
          <div className="text-black text-sm font-medium">Enter Category Images</div>
        </div>
        <div
          onClick={() => {
            imageCategoryModal();
          }}
          className="px-4 cursor-pointer py-2 bg-gray-100 rounded-md"
        >
          <div className="text-black text-xs font-semibold">+ Add New Category</div>
        </div>
      </div>
      <div className="flex flex-col">
        <div className="py-4 justify-start items-start gap-10 inline-flex">
          <div className="p-2.5 justify-start items-start gap-2.5 flex">
            <div className="">
              <span className="text-black text-xs font-normal">
                Clinic Listing Page
                <br />
              </span>
              <span className="text-slate-400 text-[10px] font-medium">(.png format, 328px x 160px and 50 KB)</span>
            </div>
          </div>
          <div className="py-2.5 flex-col justify-start items-start gap-10 inline-flex">
            {renderDefaultCategoryImages("clinic_listing_page", "gap-3")}
            {Object.entries(listcategories).length > 0 &&
              renderCustomCategoryImages(listcategories, "listing_category", "gap-3")}
          </div>
        </div>
        <div className="py-4 mb-20 justify-start items-start gap-10 inline-flex">
          <div className="p-2.5 justify-start items-start gap-2.5 inline-flex">
            <div className="">
              <span className="text-black text-xs font-normal">
                Clinic Details Page
                <br />
              </span>
              <span className="text-slate-400 text-[10px] font-medium">(.png format, 360px x 200px and 50 KB)</span>
            </div>
          </div>
          <div className="py-2.5 flex-col justify-start items-start gap-10 inline-flex">
            {renderDefaultCategoryImages("clinic_details_page", "gap-2")}
            {Object.entries(detailcategories).length > 0 &&
              renderCustomCategoryImages(detailcategories, "details_category", "gap-2")}
          </div>
        </div>
      </div>
    </>
  );
};

export default ClinicOnboardImagesStep;
